import type {
  AccessRole,
  AgencyStructureNode,
  UserRoleAssignment
} from '@gat-ship/types'

export interface AgencyStructureWithAccessRoles {
  agencyNodeId: string
  agencyNodeName: string
  agencyNodeLevel: number
  assignedAccessRoles: {
    roleId: string
    roleAssignmentId: string
    roleName: string
  }[]
  inheritedAccessRoles: {
    roleId: string
    roleName: string
  }[]
}

export class TenantUserAccessRolesUtils {
  /**
   * Creates a map of the agency structure with assigned and inherited access roles.
   *
   * The agency structure is a tree where:
   * - Each node can have directly assigned roles
   * - Children inherit all roles from their parent nodes
   * - Role inheritance follows the tree hierarchy (parent -> child -> grandchild)
   * - Duplicate roles are automatically deduplicated in inherited roles
   *
   * @param agencyStructure - The complete agency structure tree
   * @param accessRoles - All available access roles
   * @param roleAssignments - Role assignments for the current user
   * @returns Array of agency nodes with their assigned and inherited roles
   */
  static createAgencyStructureWithAccessRoles(
    agencyStructure: AgencyStructureNode[],
    accessRoles: AccessRole[],
    roleAssignments: UserRoleAssignment[]
  ): AgencyStructureWithAccessRoles[] {
    const agencyStructureWithAccessRoles: AgencyStructureWithAccessRoles[] = []

    // Create a map for quick access to access roles by ID
    const accessRolesMap = new Map<string, AccessRole>()
    for (const role of accessRoles) {
      accessRolesMap.set(role.id, role)
    }

    // Create a map for quick access to agency nodes by ID
    const agencyNodesMap = new Map<string, AgencyStructureNode>()
    for (const node of agencyStructure) {
      agencyNodesMap.set(node.id, node)
    }

    const roleAssignmentsByAgency =
      this.groupAssignmentsByAgency(roleAssignments)

    // Process each agency node
    for (const agency of agencyStructure) {
      if (agency.isInactive) {
        continue
      }
      agencyStructureWithAccessRoles.push({
        agencyNodeId: agency.id,
        agencyNodeName: agency.name,
        agencyNodeLevel: agency.level,
        // Directly assigned roles for this agency
        assignedAccessRoles: this.mapAssignedRoles(
          roleAssignmentsByAgency.get(agency.id) || [],
          accessRolesMap
        ),
        // Inherited roles from all parent nodes
        inheritedAccessRoles: this.getInheritedRoles(
          agency,
          agencyNodesMap,
          roleAssignmentsByAgency,
          accessRolesMap
        )
      })
    }

    return agencyStructureWithAccessRoles
  }

  /** Group role assignments by the agency node they're assigned on. */
  private static groupAssignmentsByAgency(
    roleAssignments: UserRoleAssignment[]
  ): Map<string, UserRoleAssignment[]> {
    const byAgency = new Map<string, UserRoleAssignment[]>()
    for (const assignment of roleAssignments) {
      if (!byAgency.has(assignment.agencyNodeId)) {
        byAgency.set(assignment.agencyNodeId, [])
      }
      byAgency.get(assignment.agencyNodeId)!.push(assignment)
    }
    return byAgency
  }

  private static mapAssignedRoles(
    assignments: UserRoleAssignment[],
    accessRolesMap: Map<string, AccessRole>
  ): AgencyStructureWithAccessRoles['assignedAccessRoles'] {
    return assignments.map((roleAssignment) => ({
      roleId: roleAssignment.roleId,
      roleAssignmentId: roleAssignment.roleAssignmentId,
      roleName: accessRolesMap.get(roleAssignment.roleId)?.name ?? ''
    }))
  }

  /**
   * Traverses up the agency tree to collect all inherited roles from parent nodes.
   *
   * @param agency - The current agency node
   * @param agencyNodesMap - Map of all agency nodes for quick lookup
   * @param roleAssignmentsByAgency - Map of role assignments grouped by agency node ID
   * @param accessRolesMap - Map of access roles for quick lookup
   * @returns Array of inherited roles (deduplicated)
   */
  private static getInheritedRoles(
    agency: AgencyStructureNode,
    agencyNodesMap: Map<string, AgencyStructureNode>,
    roleAssignmentsByAgency: Map<string, UserRoleAssignment[]>,
    accessRolesMap: Map<string, AccessRole>
  ): { roleId: string; roleName: string }[] {
    const inheritedRoles: { roleId: string; roleName: string }[] = []
    const processedRoleIds = new Set<string>() // To avoid duplicates

    let currentNode: AgencyStructureNode | undefined = agency

    // Traverse up the tree to find all parent nodes
    while (currentNode?.parentGroup) {
      const parentId = currentNode.parentGroup.id
      const parentNode = agencyNodesMap.get(parentId)

      if (!parentNode) {
        break
      }

      // Get roles assigned to the parent node
      const parentRoles = roleAssignmentsByAgency.get(parentId) || []

      for (const roleAssignment of parentRoles) {
        // Only add if we haven't processed this role ID yet
        if (!processedRoleIds.has(roleAssignment.roleId)) {
          const accessRole = accessRolesMap.get(roleAssignment.roleId)
          inheritedRoles.push({
            roleId: roleAssignment.roleId,
            roleName: accessRole?.name ?? ''
          })
          processedRoleIds.add(roleAssignment.roleId)
        }
      }

      // Move to the parent node
      currentNode = parentNode
    }

    return inheritedRoles
  }

  /**
   * The access roles that can still be assigned on an agency node: every role
   * not already assigned to or inherited by that node.
   *
   * @param agencyStructureWithAccessRoles - Agency nodes with their resolved roles.
   * @param agencyNodeId - The node to compute assignable roles for.
   * @param accessRoles - All available access roles.
   * @returns The assignable roles, or `[]` when the node is unknown.
   */
  static selectAssignableAccessRoles(
    agencyStructureWithAccessRoles: readonly AgencyStructureWithAccessRoles[],
    agencyNodeId: string,
    accessRoles: readonly AccessRole[]
  ): AccessRole[] {
    const agencyWithAccessRoles = agencyStructureWithAccessRoles.find(
      (i) => i.agencyNodeId === agencyNodeId
    )

    if (!agencyWithAccessRoles) {
      return []
    }

    const assignedRoles = [
      ...agencyWithAccessRoles.inheritedAccessRoles,
      ...agencyWithAccessRoles.assignedAccessRoles
    ]

    return accessRoles.filter(
      (role) => !assignedRoles.some((r) => r.roleId === role.id)
    )
  }

  /**
   * Whether an agency node has neither directly assigned nor inherited roles.
   */
  static hasNoAccessRoles(
    agencyStructureWithAccessRole: AgencyStructureWithAccessRoles
  ): boolean {
    return (
      agencyStructureWithAccessRole.assignedAccessRoles.length === 0 &&
      agencyStructureWithAccessRole.inheritedAccessRoles.length === 0
    )
  }
}
